import { useCallback, useEffect, useMemo, useState } from "react";

import { useDispatch, useSelector } from "react-redux";

import { useTranslate } from "@similarweb/i18n-provider";
import Sentry from "@similarweb/sentry-logger";

import { FIRST_TIME_SALESFORCE_ACTIVITY } from "app/pages/sales-intelligence/modules/onboarding/constants/preferences";
import { isWeekPassed } from "app/pages/sales-intelligence/pages/manage-integration/utils/isWeekPassed";
import {
    setIsSfFeedEnabledThunk,
    setSfCrmFeedLevelThunk,
} from "app/pages/sales-intelligence/sub-modules/management/store/effects/salesforce/toggles";
import {
    selectSalesforceFeedLevel,
    selectSalesforceFeedToggleState,
} from "app/pages/sales-intelligence/sub-modules/management/store/selectors/salesforceSelectors";
import { CrmFeedLevel } from "app/pages/sales-intelligence/sub-modules/management/types";
import { is } from "app/pages/sales-intelligence/utils/type-guards";
import { PreferencesService } from "app/services/preferences/preferencesService";

import manageIntegrationTrackingService from "../../services/tracking/manageIntegrationTrackingService";
import {
    MANAGEMENT_INTEGRATION_SF_ACTIVITY_DESCRIPTION,
    MANAGEMENT_INTEGRATION_SF_ACTIVITY_FEED_LEVEL_ACCOUNT,
    MANAGEMENT_INTEGRATION_SF_ACTIVITY_FEED_LEVEL_USER,
    MANAGEMENT_INTEGRATION_SF_ACTIVITY_TITLE,
} from "../../../../common-components/export-table/translations";
import { IntegrationSetting } from "../IntegrationSetting";

import { SalesforceActivityBanner } from "./SalesforceActivityBanner";

export const SalesforceActivitySettings = () => {
    const dispatch = useDispatch();
    const { translate } = useTranslate();

    const [firstTimeSalesforceActivity, setFirstTimeSalesforceActivity] = useState<number | undefined>(() =>
        PreferencesService.get(FIRST_TIME_SALESFORCE_ACTIVITY),
    );
    const isEnabled = useSelector(selectSalesforceFeedToggleState);
    const feedLevel = useSelector(selectSalesforceFeedLevel);

    const isShowTnCBanner =
        !is.undefined(firstTimeSalesforceActivity) && !isWeekPassed(new Date(firstTimeSalesforceActivity));

    const setPreference = () => {
        PreferencesService.add({
            [FIRST_TIME_SALESFORCE_ACTIVITY]: Date.now(),
        })
            .then((newPreferences) => {
                if (!newPreferences || !newPreferences[FIRST_TIME_SALESFORCE_ACTIVITY]) {
                    return;
                }

                setFirstTimeSalesforceActivity(newPreferences[FIRST_TIME_SALESFORCE_ACTIVITY]);
            })
            .catch((err) => {
                Sentry.error(`Preferences service: error adding preference (${FIRST_TIME_SALESFORCE_ACTIVITY})`, err);
            });
    };

    // set banner time if SalesforceActivity turned on OR for old users who already has SalesforceActivity turned on
    useEffect(() => {
        if (isEnabled && is.undefined(firstTimeSalesforceActivity)) {
            setPreference();
        }
    }, [isEnabled]);

    const handleToggle = (isEnabledToggle: boolean) => {
        manageIntegrationTrackingService.trackSalesforceActivityToggle(isEnabledToggle);

        dispatch(setIsSfFeedEnabledThunk(isEnabledToggle));
    };

    const handleSelect = useCallback(
        (value: CrmFeedLevel) => {
            dispatch(setSfCrmFeedLevelThunk(value));
        },
        [dispatch],
    );

    const dropdownProps = useMemo(
        () => ({
            options: [
                { value: CrmFeedLevel.User, label: translate(MANAGEMENT_INTEGRATION_SF_ACTIVITY_FEED_LEVEL_USER) },
                {
                    value: CrmFeedLevel.Account,
                    label: translate(MANAGEMENT_INTEGRATION_SF_ACTIVITY_FEED_LEVEL_ACCOUNT),
                },
            ],
            value: feedLevel,
            onSelect: handleSelect,
        }),
        [feedLevel, handleSelect],
    );

    return (
        <IntegrationSetting
            dataAutomation="salesforce-activity-setting"
            badgeType="new"
            isEnabled={isEnabled}
            title={translate(MANAGEMENT_INTEGRATION_SF_ACTIVITY_TITLE)}
            content={translate(MANAGEMENT_INTEGRATION_SF_ACTIVITY_DESCRIPTION)}
            onToggle={handleToggle}
            dropdownProps={dropdownProps}
        >
            {isEnabled && isShowTnCBanner ? <SalesforceActivityBanner /> : null}
        </IntegrationSetting>
    );
};
